import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { ClaimOptionContext, IClaimOptionTypeState } from 'context/ClaimOption';
import { IFormIUData } from 'types/form';
import { IFormNavigationProps } from './types';
import { SCButtonWrapper, SCElementsWrapper, SCRowWrapper } from './styles';
import { TimelineState } from 'enums/TimeLine';
import { ClaimTypesEnum } from 'enums/ClaimTypes';
import { allFieldsComplete, allFieldsValid } from 'utils/form/form';
import { TSButton } from 'components/atoms/TSButton';
import { TSButtonSizeEnum, TSButtonVariantsEnum } from 'enums/TSButtonVariants';
import { ClaimStateEnum } from 'enums/ClaimState';
import { ClaimFormAdvancementContext, IClaimFormAdvancementTypeState } from 'context/ClaimFormAdvancement';
import { TSDialog } from 'components/atoms/TSDialog';
import {
    $digitalBlack,
    SCContentSectionResponsiveWrapper,
    SCContentSectionWrapper,
    SCSectionBody,
    SCSectionSubHead
} from 'styles/global-styles';
import { FlexAlignItemsEnum, FlexDirectionEnum, FlexJustifyContentEnum } from 'enums/StyleTypes';
import { ClaimFormDisabilityContext, IClaimFormDisabilityTypeState } from 'context/ClaimFormDisability';
import { ClaimFormJobLossContext, IClaimFormJobLossTypeState } from 'context/ClaimFormJobLoss';
import { ClaimProgressJobLossContext, IClaimProgressJobLossTypeState } from 'context/ClaimProgressJobLoss';
import { ClaimProgressDisabilityContext, IClaimProgressDisabilityTypeState } from 'context/ClaimProgressDisability';
import { DistributorDataContext, IDistributorDataTypeState } from 'context/DistributorData';

// Constants for step IDs - centralized for easy maintenance
const STEP_IDS = {
    FIRST_STEP: 'id-1',
    JOBLOSS_COMMENTS: 'id-4',
    JOBLOSS_PAYMENT_DETAILS: 'id-5',
    DISABILITY_COMMENTS: 'id-5',
    DISABILITY_PAYMENT_DETAILS: 'id-6'
} as const;
const FormNavigation = ({
    formData,
    requiredFields,
    validationFields,
    isFormDirty
}: IFormNavigationProps): ReactElement => {
    // Helper to increment/decrement step IDs
    const adjustStepId = (id: string, adjustment: number): string =>
        id.replace(/\d+$/, (n) => String(parseInt(n) + adjustment));
    // Context - only destructure what's actually used
    const {
        updateContextStateClaimProgressJobLoss,
        contextStateClaimProgressJobLoss,
        claimProgressJobLossCompletionStatus,
        updateAllClaimProgressJobLossCompletionStatus
    } = React.useContext(ClaimProgressJobLossContext) as IClaimProgressJobLossTypeState;
    const {
        contextStateClaimProgressDisability,
        updateContextStateClaimProgressDisability,
        claimProgressDisabilityCompletionStatus,
        updateAllClaimProgressDisabilityCompletionStatus
    } = React.useContext(ClaimProgressDisabilityContext) as IClaimProgressDisabilityTypeState;
    const { contextStateClaimOption, updateContextStateClaimOption } = React.useContext(
        ClaimOptionContext
    ) as IClaimOptionTypeState;
    const { updateContextStateClaimFormAdvancement } = React.useContext(
        ClaimFormAdvancementContext
    ) as IClaimFormAdvancementTypeState;
    const { updateContextStateClaimFormJobLoss } = React.useContext(
        ClaimFormJobLossContext
    ) as IClaimFormJobLossTypeState;
    const { updateContextStateClaimFormDisability } = React.useContext(
        ClaimFormDisabilityContext
    ) as IClaimFormDisabilityTypeState;
    const { contextStateDistributorData } = React.useContext(DistributorDataContext) as IDistributorDataTypeState;
    // Derived state - computed once and reused
    const isAdvantageDistributor = contextStateDistributorData?.distributorKey === 'advantage';
    const isJobLoss = contextStateClaimOption.type === ClaimTypesEnum.Jobloss;
    const isDisability = contextStateClaimOption.type === ClaimTypesEnum.Disability;
    const currentProgressState = isJobLoss ? contextStateClaimProgressJobLoss : contextStateClaimProgressDisability;
    // Local state
    const [formValid, setFormValid] = useState<boolean>(false);
    const [confirmModalIsVisible, setConfirmModalIsVisible] = useState(false);
    // Memoized values - prevent unnecessary recalculations
    const currentId = useMemo(() => {
        const activeItem = currentProgressState.find((item) => item.status === TimelineState.ACTIVE);
        return activeItem?.id ?? '';
    }, [currentProgressState]);
    const incrementId = useMemo(() => adjustStepId(currentId, 1), [currentId]);
    const decrementId = useMemo(() => adjustStepId(currentId, -1), [currentId]);
    const showReturnToReview =
        (isDisability && claimProgressDisabilityCompletionStatus) ||
        (isJobLoss && claimProgressJobLossCompletionStatus);

    // Effects
    useEffect(() => {
        if (formData && requiredFields) {
            setFormValid(allFieldsComplete(requiredFields, formData) && allFieldsValid(validationFields));
        }
    }, [formData, requiredFields, validationFields]);

    // Callbacks - memoized to prevent unnecessary re-renders
    const handleSaveFormState = useCallback(
        (formDataToSave: IFormIUData | any): void => {
            if (isJobLoss) {
                updateContextStateClaimFormJobLoss(formDataToSave);
            } else {
                updateContextStateClaimFormDisability(formDataToSave);
            }
        },
        [isJobLoss, updateContextStateClaimFormJobLoss, updateContextStateClaimFormDisability]
    );
    const goToReview = useCallback(
        (claimType: ClaimTypesEnum): void => {
            updateContextStateClaimOption({
                type: claimType,
                reviewState: ClaimStateEnum.REVIEW
            });
            updateContextStateClaimFormAdvancement({
                reviewState: ClaimStateEnum.REVIEW
            });
        },
        [updateContextStateClaimOption, updateContextStateClaimFormAdvancement]
    );
    const handleBackFormStates = useCallback((): void => {
        if (currentId === STEP_IDS.FIRST_STEP) {
            updateContextStateClaimOption({
                type: undefined,
                reviewState: ClaimStateEnum.ACTIVE
            });
            updateContextStateClaimProgressJobLoss(currentId, TimelineState.COMPLETE);
            updateContextStateClaimProgressDisability(currentId, TimelineState.COMPLETE);
            setConfirmModalIsVisible(true);
            return;
        }

        if (isJobLoss) {
            updateContextStateClaimProgressJobLoss(decrementId, TimelineState.ACTIVE);
            updateContextStateClaimProgressJobLoss(currentId, TimelineState.COMPLETE);
        } else if (isDisability) {
            updateContextStateClaimProgressDisability(decrementId, TimelineState.ACTIVE);
            updateContextStateClaimProgressDisability(currentId, TimelineState.COMPLETE);
        }
    }, [
        currentId,
        decrementId,
        isJobLoss,
        isDisability,
        updateContextStateClaimOption,
        updateContextStateClaimProgressJobLoss,
        updateContextStateClaimProgressDisability
    ]);
    const handleContinue = useCallback((): void => {
        handleSaveFormState(formData);

        if (!allFieldsComplete(requiredFields, formData)) {
            return;
        }

        if (isJobLoss) {
            updateContextStateClaimProgressJobLoss(currentId, TimelineState.COMPLETE);

            // Skip Claim Payment Details for non-advantage distributors
            const isOnCommentsStep = currentId === STEP_IDS.JOBLOSS_COMMENTS;
            if (!isAdvantageDistributor && isOnCommentsStep) {
                updateContextStateClaimProgressJobLoss(STEP_IDS.JOBLOSS_PAYMENT_DETAILS, TimelineState.COMPLETE);
                goToReview(ClaimTypesEnum.Jobloss);
                return;
            }

            updateContextStateClaimProgressJobLoss(incrementId, TimelineState.ACTIVE);

            // Check if all steps complete
            if (contextStateClaimProgressJobLoss.every((obj) => obj.status === TimelineState.COMPLETE)) {
                goToReview(ClaimTypesEnum.Jobloss);
            }
        }

        if (isDisability) {
            updateContextStateClaimProgressDisability(currentId, TimelineState.COMPLETE);

            // Skip Claim Payment Details for non-advantage distributors
            const isOnCommentsStep = currentId === STEP_IDS.DISABILITY_COMMENTS;
            if (!isAdvantageDistributor && isOnCommentsStep) {
                updateContextStateClaimProgressDisability(STEP_IDS.DISABILITY_PAYMENT_DETAILS, TimelineState.COMPLETE);
                goToReview(ClaimTypesEnum.Disability);
                return;
            }

            updateContextStateClaimProgressDisability(incrementId, TimelineState.ACTIVE);

            // Check if all steps complete
            if (contextStateClaimProgressDisability.every((obj) => obj.status === TimelineState.COMPLETE)) {
                goToReview(ClaimTypesEnum.Disability);
            }
        }
    }, [
        formData,
        requiredFields,
        isJobLoss,
        isDisability,
        isAdvantageDistributor,
        currentId,
        incrementId,
        handleSaveFormState,
        goToReview,
        updateContextStateClaimProgressJobLoss,
        updateContextStateClaimProgressDisability,
        contextStateClaimProgressJobLoss,
        contextStateClaimProgressDisability
    ]);
    const handleReturnToReview = useCallback((): void => {
        handleSaveFormState(formData);

        if (!allFieldsComplete(requiredFields, formData)) {
            return;
        }

        if (isJobLoss) {
            goToReview(ClaimTypesEnum.Jobloss);
            updateAllClaimProgressJobLossCompletionStatus();
        }

        if (isDisability) {
            goToReview(ClaimTypesEnum.Disability);
            updateAllClaimProgressDisabilityCompletionStatus();
        }
    }, [
        formData,
        requiredFields,
        isJobLoss,
        isDisability,
        handleSaveFormState,
        goToReview,
        updateAllClaimProgressJobLossCompletionStatus,
        updateAllClaimProgressDisabilityCompletionStatus
    ]);
    const closeModal = useCallback((): void => setConfirmModalIsVisible(false), []);
    const handleBackClick = useCallback((): void => {
        if (isFormDirty && currentId === STEP_IDS.FIRST_STEP) {
            setConfirmModalIsVisible(true);
        } else {
            handleBackFormStates();
        }
    }, [isFormDirty, currentId, handleBackFormStates]);

    return (
        <SCElementsWrapper>
            <SCRowWrapper>
                <SCButtonWrapper>
                    <TSButton
                        tsVariant={TSButtonVariantsEnum.BACK}
                        onClick={handleBackClick}
                        ariaLabel={'return to previous view'}
                    >
                        Back
                    </TSButton>
                </SCButtonWrapper>
                <SCButtonWrapper>
                    <TSButton
                        tsVariant={TSButtonVariantsEnum.ARROW}
                        ariaLabel={'continue to next step'}
                        disabled={!formValid}
                        onClick={handleContinue}
                    >
                        Continue
                    </TSButton>
                </SCButtonWrapper>
            </SCRowWrapper>

            {showReturnToReview && (
                <SCRowWrapper>
                    <SCButtonWrapper>
                        <TSButton
                            tsVariant={TSButtonVariantsEnum.BACK}
                            disabled={!formValid}
                            onClick={handleReturnToReview}
                            ariaLabel={'return to review page'}
                        >
                            Return to Review
                        </TSButton>
                    </SCButtonWrapper>
                </SCRowWrapper>
            )}

            <TSDialog
                ariaLabel={'Unsaved Changes dialog'}
                hideSecondaryCloseButton
                handleClose={closeModal}
                id={'unsavedChangesDialog'}
                open={confirmModalIsVisible}
                dialogSize={'sm'}
                bannerHeaderCloseButtonText={'Close'}
                content={
                    <SCContentSectionWrapper
                        flexDirection={FlexDirectionEnum.COLUMN}
                        flexAlignment={FlexAlignItemsEnum.FLEX_START}
                        flexGap={'24px'}
                    >
                        <SCSectionSubHead textColor={$digitalBlack}>Unsaved Changes</SCSectionSubHead>
                        <SCSectionBody>
                            You have unsaved claim information, continuing may cause your information to be lost
                        </SCSectionBody>
                    </SCContentSectionWrapper>
                }
                actionChildren={
                    <SCContentSectionResponsiveWrapper
                        flexJustifyContent={FlexJustifyContentEnum.SPACE_AROUND}
                        flexAlignment={FlexAlignItemsEnum.CENTER}
                        flexDirection={FlexDirectionEnum.COLUMN}
                        flexGap={'16px'}
                    >
                        <TSButton
                            ariaLabel="dialog return to page"
                            tsVariant={TSButtonVariantsEnum.TEXT}
                            size={TSButtonSizeEnum.MEDIUM}
                            onClick={closeModal}
                        >
                            Return to page
                        </TSButton>
                        <TSButton
                            ariaLabel="dialog proceed without saving"
                            tsVariant={TSButtonVariantsEnum.ARROW}
                            size={TSButtonSizeEnum.MEDIUM}
                            onClick={(): void => {
                                handleBackFormStates();
                                closeModal();
                            }}
                        >
                            Proceed without saving
                        </TSButton>
                    </SCContentSectionResponsiveWrapper>
                }
            />
        </SCElementsWrapper>
    );
};

export { FormNavigation };
